import { useState, useEffect } from "react"
import { BarChart3, TrendingUp, Users, Loader2, ChevronDown } from "lucide-react"
import { CampaignService } from "@/features/campaigns"
import { SubmissionStats } from "@/features/campaigns"

// Shimmer Loading Components with gradient animation and staggered delays
const ShimmerStatsCard = ({ delay = 0 }: { delay?: number }) => (
  <div className="bg-gray-200 rounded-2xl shadow-lg p-6 relative overflow-hidden" style={{ animationDelay: `${delay}ms` }}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
    <div className="text-center relative">
      <div className="h-12 bg-gray-300 rounded-lg mb-2 animate-pulse"></div>
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-1 mx-auto animate-pulse"></div>
      <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto animate-pulse"></div>
    </div>
  </div>
)

const ShimmerMetricCard = ({ delay = 0 }: { delay?: number }) => (
  <div className="bg-white border-2 border-gray-200 rounded-xl p-6 relative overflow-hidden" style={{ animationDelay: `${delay}ms` }}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-gray-100/30 to-transparent"></div>
    <div className="text-center relative">
      <div className="h-10 bg-gray-300 rounded-lg mb-2 animate-pulse"></div>
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-1 mx-auto animate-pulse"></div>
      <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto animate-pulse"></div>
    </div>
  </div>
)

const ShimmerHeader = () => (
  <div className="flex items-center gap-4 mb-8">
    <div className="w-12 h-12 bg-gray-300 rounded-xl animate-pulse"></div>
    <div className="flex-1">
      <div className="h-6 bg-gray-300 rounded-lg mb-2 w-64 animate-pulse"></div>
      <div className="h-4 bg-gray-300 rounded w-48 animate-pulse"></div>
    </div>
  </div>
)

interface ViewDashboardProps {
  campaignName?: string
}

export default function ViewDashboard({ campaignName }: ViewDashboardProps) {
  const [stats, setStats] = useState<SubmissionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeFilter, setTimeFilter] = useState<string>('TOTAL')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(5) // Show 5 organizations per page
  const [totalPages, setTotalPages] = useState(0)
  const [totalOrganizations, setTotalOrganizations] = useState(0)

  // Add shimmer animation styles
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (isDropdownOpen && !target.closest('.dropdown-container')) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const toggleCardExpansion = (orgId: string) => {
    const newExpanded = new Set(expandedCards)
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId)
    } else {
      newExpanded.add(orgId)
    }
    setExpandedCards(newExpanded)
  }

  // Pagination logic - now using server-side pagination
  const getPaginatedOrganizations = () => {
    return stats?.organizationDistribution || []
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Collapse all expanded cards when changing pages
    setExpandedCards(new Set())
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await CampaignService.getSubmissionStats(undefined, timeFilter, currentPage, itemsPerPage)
        setStats(response.data || null)
        
        // Update pagination info from backend response
        if (response.data?.organizationPagination) {
          setTotalPages(response.data.organizationPagination.totalPages)
          setTotalOrganizations(response.data.organizationPagination.totalOrganizations)
        } else {
          // Fallback to client-side pagination if backend doesn't provide pagination info
          setTotalPages(stats?.organizationDistribution ? Math.ceil(stats.organizationDistribution.length / itemsPerPage) : 0)
          setTotalOrganizations(stats?.organizationDistribution?.length || 0)
        }
        
        setExpandedCards(new Set())
      } catch (err: any) {
        console.error("Error fetching submission stats:", err)
        setError("Failed to load submission statistics")
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [timeFilter, currentPage])
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Campaign Statistics Overview Shimmer */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-b border-gray-200">
            <ShimmerHeader />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              <ShimmerStatsCard delay={0} />
              <ShimmerStatsCard delay={100} />
              <ShimmerStatsCard delay={200} />
              <ShimmerStatsCard delay={300} />
              <ShimmerStatsCard delay={400} />
            </div>
          </div>

          {/* Organization Performance Shimmer */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8">
            <ShimmerHeader />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <ShimmerMetricCard delay={0} />
              <ShimmerMetricCard delay={150} />
              <ShimmerMetricCard delay={300} />
              <ShimmerMetricCard delay={450} />
            </div>
          </div>

          {/* Performance Metrics Shimmer */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 border-t border-gray-200">
            <ShimmerHeader />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <ShimmerMetricCard delay={0} />
              <ShimmerMetricCard delay={200} />
              <ShimmerMetricCard delay={400} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">
          <div className="text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Submission Statistics</div>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">
          <div className="text-center">
            <div className="text-gray-600 text-lg font-semibold mb-2">No Submission Statistics Available</div>
            <p className="text-gray-500">Unable to load submission statistics</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Time Filter Section */}
      <div className="mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Time Period Filter</h3>
              <p className="text-sm text-gray-600">Filter statistics by time period</p>
            </div>
            <div className="relative dropdown-container">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[140px]"
              >
                <span className="font-medium text-gray-700">{timeFilter}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEARLY', 'TOTAL'].map((period) => (
                    <button
                      key={period}
                      onClick={() => {
                        setTimeFilter(period)
                        setIsDropdownOpen(false)
                      }}
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${timeFilter === period
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                          : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Container with Enhanced Styling */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">

        {/* Submission Statistics Overview */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-b border-gray-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Submission Statistics Overview</h2>
              <p className="text-gray-600">Complete breakdown of all submission statuses (Steps 9 & 10 completed) - {timeFilter} view</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{stats.successfulSubmissions}</div>
                <div className="text-sm font-semibold mb-1">Successful Submissions</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalSubmissions > 0 ? Math.round((stats.successfulSubmissions / stats.totalSubmissions) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{stats.pendingSubmissions}</div>
                <div className="text-sm font-semibold mb-1">Pending Submissions</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalSubmissions > 0 ? Math.round((stats.pendingSubmissions / stats.totalSubmissions) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{stats.rejectedSubmissions}</div>
                <div className="text-sm font-semibold mb-1">Rejected Submissions</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalSubmissions > 0 ? Math.round((stats.rejectedSubmissions / stats.totalSubmissions) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{stats.vendorNeedsInfoSubmissions}</div>
                <div className="text-sm font-semibold mb-1">Vendor Needs Additional Info</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalSubmissions > 0 ? Math.round((stats.vendorNeedsInfoSubmissions / stats.totalSubmissions) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{stats.totalSubmissions}</div>
                <div className="text-sm font-semibold mb-1">Total Submissions</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">100% of All</div>
              </div>
            </div>
          </div>
        </div>


        {/* Pending Application Counts (Steps 1-8) */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Pending Application Counts</h2>
              <p className="text-gray-600">Applications in progress (Steps 1-8) - {timeFilter} view</p>
            </div>
          </div>

          {/* Responsive Grid Layout: 1 col (small) / 2 cols (md) / 3 cols (lg) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
            {/* Step 1 Apps - Dark Red */}
            <div className="bg-gradient-to-br from-red-900 to-red-800 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step1Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 1 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step1Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 2 Apps - Red */}
            <div className="bg-gradient-to-br from-red-600 to-red-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step2Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 2 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step2Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 3 Apps - Orange */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step3Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 3 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step3Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 4 Apps - Yellow */}
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step4Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 4 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step4Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 5 Apps - Green */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step5Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 5 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step5Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 6 Apps - Blue */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step6Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 6 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step6Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 7 Apps - Indigo */}
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step7Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 7 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step7Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Step 8 Apps - Violet */}
            <div className="bg-gradient-to-br from-violet-500 to-violet-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.step8Apps}</div>
                <div className="text-sm font-semibold mb-2">Step 8 Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.step8Apps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Deleted Apps - Black */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.deletedApps}</div>
                <div className="text-sm font-semibold mb-2">Deleted Apps</div>
                <div className="text-xs opacity-90 bg-white/20 rounded-full px-3 py-1">
                  {stats.totalApps > 0 ? Math.round((stats.deletedApps / stats.totalApps) * 100) : 0}% of Total
                </div>
              </div>
            </div>

            {/* Total Apps - White */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-800 border-2 border-gray-300 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.totalApps}</div>
                <div className="text-sm font-semibold mb-2">Total Apps</div>
                <div className="text-xs bg-gray-300 rounded-full px-3 py-1">
                  100% of All
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organizational Performance */}
        <div className="bg-gray-50 p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Organization Performance</h2>
            </div>
            <p className="text-sm text-gray-600 ml-11">{timeFilter} view</p>
          </div>

          {stats.organizationDistribution && stats.organizationDistribution.length > 0 ? (
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Table Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600">
                  <div className="col-span-4">Organization</div>
                  <div className="col-span-2 text-center">Total Submissions</div>
                  <div className="col-span-2 text-center">Success Rate</div>
                  <div className="col-span-4 text-center">Status Distribution</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {getPaginatedOrganizations().map((org, index) => {
                  const isExpanded = expandedCards.has(org.organizationId)
                  const successRate = org.totalSubmissions > 0 ? Math.round((org.successfulSubmissions / org.totalSubmissions) * 100) : 0

                  return (
                    <div key={`org-row-${org.organizationId}-${index}`}>
                      {/* Main Row */}
                      <div 
                        className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleCardExpansion(org.organizationId)}
                      >
                        <div className="grid grid-cols-12 gap-4 items-center">
                          {/* Organization Info */}
                          <div className="col-span-4">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">{org.organizationName}</h3>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              ID: {org.organizationId}
                            </span>
                          </div>

                          {/* Total Submissions */}
                          <div className="col-span-2 text-center">
                            <span className="text-2xl font-bold text-gray-900">{org.totalSubmissions}</span>
                          </div>

                          {/* Success Rate */}
                          <div className="col-span-2 text-center">
                            <span className={`text-2xl font-bold ${successRate >= 75 ? 'text-green-600' :
                                successRate >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }`}>{successRate}%</span>
                          </div>

                          {/* Status Distribution */}
                          <div className="col-span-4">
                            <div className="flex items-center justify-center gap-4">
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-900">{org.successfulSubmissions}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-900">{org.pendingSubmissions}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-900">{org.rejectedSubmissions}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-900">{org.needsInfoSubmissions}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        isExpanded 
                          ? 'max-h-[2000px] opacity-100' 
                          : 'max-h-0 opacity-0'
                      }`}>
                        <div className="px-6 py-6 bg-gray-50 border-t border-gray-200">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                            {/* Left Column - Submission Status Details */}
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 mb-4">Detailed Status Breakdown</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-xl text-center shadow-md">
                                  <div className="text-2xl font-bold mb-1">{org.successfulSubmissions}</div>
                                  <div className="text-sm font-medium opacity-90">Successful</div>
                                  <div className="text-xs opacity-75 mt-1">
                                    {org.totalSubmissions > 0 ? Math.round((org.successfulSubmissions / org.totalSubmissions) * 100) : 0}%
                                  </div>
                                </div>

                                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-4 rounded-xl text-center shadow-md">
                                  <div className="text-2xl font-bold mb-1">{org.pendingSubmissions}</div>
                                  <div className="text-sm font-medium opacity-90">Pending</div>
                                  <div className="text-xs opacity-75 mt-1">
                                    {org.totalSubmissions > 0 ? Math.round((org.pendingSubmissions / org.totalSubmissions) * 100) : 0}%
                                  </div>
                                </div>

                                <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-4 rounded-xl text-center shadow-md">
                                  <div className="text-2xl font-bold mb-1">{org.rejectedSubmissions}</div>
                                  <div className="text-sm font-medium opacity-90">Rejected</div>
                                  <div className="text-xs opacity-75 mt-1">
                                    {org.totalSubmissions > 0 ? Math.round((org.rejectedSubmissions / org.totalSubmissions) * 100) : 0}%
                                  </div>
                                </div>

                                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-xl text-center shadow-md">
                                  <div className="text-2xl font-bold mb-1">{org.needsInfoSubmissions}</div>
                                  <div className="text-sm font-medium opacity-90">Needs Info</div>
                                  <div className="text-xs opacity-75 mt-1">
                                    {org.totalSubmissions > 0 ? Math.round((org.needsInfoSubmissions / org.totalSubmissions) * 100) : 0}%
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Right Column - Application Steps & Summary */}
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 mb-4">Application Progress</h4>

                              {/* Application Steps */}
                              <div className="mb-6">
                                <h5 className="text-sm font-medium text-gray-700 mb-3">Steps Distribution (1-8)</h5>
                                <div className="grid grid-cols-4 gap-2">
                                  <div className="bg-gradient-to-br from-red-900 to-red-800 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step1Apps}</div>
                                    <div className="text-xs opacity-90">Step 1</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step1Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-red-600 to-red-700 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step2Apps}</div>
                                    <div className="text-xs opacity-90">Step 2</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step2Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step3Apps}</div>
                                    <div className="text-xs opacity-90">Step 3</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step3Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step4Apps}</div>
                                    <div className="text-xs opacity-90">Step 4</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step4Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step5Apps}</div>
                                    <div className="text-xs opacity-90">Step 5</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step5Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step6Apps}</div>
                                    <div className="text-xs opacity-90">Step 6</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step6Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step7Apps}</div>
                                    <div className="text-xs opacity-90">Step 7</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step7Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-violet-500 to-violet-600 text-white p-3 rounded-lg text-center">
                                    <div className="text-sm font-bold">{org.applicationSteps.step8Apps}</div>
                                    <div className="text-xs opacity-90">Step 8</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.step8Apps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Summary */}
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-3">Summary</h5>
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-800 border-2 border-gray-300 p-4 rounded-lg text-center">
                                    <div className="text-lg font-bold">{org.applicationSteps.totalApps}</div>
                                    <div className="text-xs opacity-90">Total Apps</div>
                                    <div className="text-xs bg-gray-300 rounded-full px-2 py-1 mt-1">100%</div>
                                  </div>
                                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-4 rounded-lg text-center">
                                    <div className="text-lg font-bold">{org.applicationSteps.deletedApps}</div>
                                    <div className="text-xs opacity-90">Deleted</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round((org.applicationSteps.deletedApps / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg text-center">
                                    <div className="text-lg font-bold">{org.applicationSteps.totalApps - org.applicationSteps.deletedApps}</div>
                                    <div className="text-xs opacity-90">Active</div>
                                    <div className="text-xs opacity-75">
                                      {org.applicationSteps.totalApps > 0 ? Math.round(((org.applicationSteps.totalApps - org.applicationSteps.deletedApps) / org.applicationSteps.totalApps) * 100) : 0}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="bg-white px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalOrganizations)} of {totalOrganizations} organizations
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                          }`}
                      >
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${currentPage === page
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                              }`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organization Data</h3>
              <p className="text-gray-600">No submissions are currently assigned to organizations.</p>
            </div>
          )}
        </div>

        {/* Submission Performance Metrics */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 border-t border-gray-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Submission Performance Metrics</h2>
              <p className="text-gray-600">Success rates and processing insights - {timeFilter} view</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-green-300 hover:shadow-lg transition-all duration-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {stats.totalSubmissions > 0 ? Math.round((stats.successfulSubmissions / stats.totalSubmissions) * 100) : 0}%
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-1">Success Rate</div>
                <div className="text-xs text-gray-500">Successful submissions</div>
              </div>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-green-300 hover:shadow-lg transition-all duration-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {stats.totalSubmissions > 0 ? Math.round((stats.pendingSubmissions / stats.totalSubmissions) * 100) : 0}%
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-1">Pending Rate</div>
                <div className="text-xs text-gray-500">Submissions in progress</div>
              </div>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-green-300 hover:shadow-lg transition-all duration-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">
                  {stats.totalSubmissions > 0 ? Math.round((stats.rejectedSubmissions / stats.totalSubmissions) * 100) : 0}%
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-1">Rejection Rate</div>
                <div className="text-xs text-gray-500">Rejected submissions</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
